/**
 * tax/NewsletterMaker.tsx — 세무사 사이트 「월간 소식지 만들기」 (2026-10-09)
 *  왼쪽 입력(번호 붙인 한 페이지) · 오른쪽 A4 미리보기(서버가 파일과 같은 판으로 그린 HTML) · PDF 다운로드 · JPG 저장(카톡 사진용)
 *  순서(자문 #4): ① 발행 월 → ② 받는 고객 → ③ 지원사업 → ④ 이달의 이슈 → ⑤ 보내는 곳(기억해 두면 한 줄로 접힘) · 세무일정 자동
 *  휴대폰(자문 #6): 위 「입력 | 미리보기」 탭 + 아래 고정 버튼(공유 시트)
 *  2026-10-09 디자인 시안(zip) 적용: 단계 「1 / 5」 · 회색 입력칸 · 대표색 12가지 · 미리보기 칸 고정(sticky) · PDF 주 버튼(PC · 휴대폰 같게)
 *  기억: 사무소 정보 · 받는 고객 조건은 이 브라우저에만(localStorage · 막히면 이번 화면만)
 */
import React from 'react';
import { X, Chip, Btn, Label, KInput, Tag, card, inputSt, useWide, Shell, Section, Dot, Glyph } from './taxUi';
import { getMonth, getPreview, getFile, load, save, OFFICE_KEY, type TaxMonth, type TaxMonthData, type TaxCfg } from './taxApi';
import { REGION_CITIES } from '../tm/components/ui';

const COND_KEY = 'roumTax.cond';
const SIDO = Object.keys(REGION_CITIES);
const INDUSTRIES = ['제조업', '도소매업', '건설업', '음식·숙박업', 'IT·서비스업'];
const SIZES = ['소상공인', '소기업', '중기업'];
const ROLES = ['세무사', '회계사'];   // + 직접 입력 (2026-10-09 지도사 「세무사, 회계사, 직접입력 3개만」)
// 대표색 12가지(시안) — 키는 서버 소식지 판(services/newsletterBrief.js COLORS)과 같다
const COLORS: [string, string, string][] = [
  ['navy', '남색', '#1d2a44'], ['blue', '파랑', '#1f4fb8'], ['teal', '청록', '#0f5e63'], ['green', '진녹색', '#1f4d3a'], ['olive', '올리브', '#4b5320'], ['brown', '갈색', '#5c3a1e'],
  ['orange', '주황', '#b4501a'], ['burgundy', '버건디', '#5a1f2b'], ['wine', '자주', '#6b2357'], ['purple', '보라', '#3e2a7a'], ['slate', '회청색', '#3b4a5c'], ['ink', '먹색', '#2b2b2b'],
];
const MAX = 7;

interface Office { office: string; role: string; person: string; tel: string; mob: string; logo: string; color: string }
interface Cond { sido: string; sigungu: string; industry: string; sizes: string[] }
const OFFICE0: Office = { office: '', role: '세무사', person: '', tel: '', mob: '', logo: '', color: 'navy' };
const COND0: Cond = { sido: '', sigungu: '', industry: '', sizes: [] };   // 기업 규모는 하나만(빈 배열 = 규모 무관 · 2026-10-09)

const ymLabel = (ym: string) => { const [y, m] = ym.split('-').map(Number); return `${y}년 ${m}월호`; };
function phone(v: string): string {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (!d) return '';
  if (/^02/.test(d)) return d.length <= 9 ? d.replace(/^(02)(\d{1,3})?(\d{0,4})?$/, (_m, a, b, c) => [a, b, c].filter(Boolean).join('-')) : d.replace(/^(02)(\d{4})(\d{0,4})$/, '$1-$2-$3');
  if (/^1[5-9]\d{2}/.test(d) && d.length <= 8) return d.length > 4 ? `${d.slice(0, 4)}-${d.slice(4)}` : d;
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
}
/** 처음 고를 발행 월 — 공개된 달 중 다음 달까지 · 없으면 가장 최근 */
function pickYm(months: TaxMonth[]): string {
  const t = new Date(); const next = `${t.getFullYear() + (t.getMonth() === 11 ? 1 : 0)}-${String(t.getMonth() === 11 ? 1 : t.getMonth() + 2).padStart(2, '0')}`;
  const ok = months.filter((m) => m.ym <= next);
  return (ok[0] || months[0]).ym;
}

export default function NewsletterMaker({ months, onHome }: { months: TaxMonth[] | null; onHome: () => void }) {
  const wide = useWide(1024);
  const [tab, setTab] = React.useState<'input' | 'preview'>('input');
  const [ym, setYm] = React.useState('');
  const [cond, setCond] = React.useState<Cond>(() => { const c = load<Cond>(COND_KEY, COND0); return c.sizes.length === 1 ? c : { ...c, sizes: [] }; });   // 예전에 여러 개 저장했으면 「규모 무관」
  const [office, setOffice] = React.useState<Office>(() => load<Office>(OFFICE_KEY, OFFICE0));
  const [officeOpen, setOfficeOpen] = React.useState<boolean>(() => !load<Office>(OFFICE_KEY, OFFICE0).office);
  const [data, setData] = React.useState<TaxMonthData | null>(null);
  const [loadErr, setLoadErr] = React.useState('');
  const [sel, setSel] = React.useState<string[]>([]);
  const [limitMsg, setLimitMsg] = React.useState(false);
  const [issueId, setIssueId] = React.useState('');
  const [html, setHtml] = React.useState('');
  const [busy, setBusy] = React.useState<'' | 'pdf' | 'jpg'>('');
  const [done, setDone] = React.useState<{ ok: boolean; text: string } | null>(null);
  const [needOffice, setNeedOffice] = React.useState(false);
  const officeRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => { if (months && months.length && !ym) setYm(pickYm(months)); }, [months, ym]);
  React.useEffect(() => { save(COND_KEY, cond); }, [cond]);
  React.useEffect(() => { save(OFFICE_KEY, office); }, [office]);

  // 달 · 받는 고객이 바뀌면 그 범위의 추천을 다시 받고 위에서 7건을 미리 고른다
  React.useEffect(() => {
    if (!ym) return undefined;
    let alive = true;
    setLoadErr('');
    getMonth(ym, cond).then((d) => {
      if (!alive) return;
      setData(d);
      setSel(d.programs.slice(0, MAX).map((p) => p.id));
      setIssueId((cur) => (d.issues.some((i) => i.id === cur) ? cur : ((d.issues.find((i) => i.star) || d.issues[0] || { id: '' }).id)));
    }).catch((e: unknown) => { if (alive) setLoadErr(e instanceof Error ? e.message : '자료를 불러오지 못했습니다'); });
    return () => { alive = false; };
  }, [ym, cond.sido, cond.sigungu, cond.industry, cond.sizes.join(',')]);   // 시·군·구도 다시 받기(2026-10-09)   // eslint-disable-line react-hooks/exhaustive-deps

  const cfg: TaxCfg = React.useMemo(() => ({ ...office, sido: cond.sido, sigungu: cond.sigungu, industry: cond.industry, sizes: cond.sizes, program_ids: sel, issue_id: issueId }), [office, cond, sel, issueId]);

  // 미리보기 — 0.35초 쉬었다가 서버에 그려 달라고 한다(파일과 같은 판)
  React.useEffect(() => {
    if (!ym) return undefined;
    const t = window.setTimeout(() => { getPreview(ym, cfg).then((r) => setHtml(r.html)).catch(() => undefined); }, 350);
    return () => window.clearTimeout(t);
  }, [ym, cfg]);
  React.useEffect(() => { if (!limitMsg) return undefined; const t = window.setTimeout(() => setLimitMsg(false), 4000); return () => window.clearTimeout(t); }, [limitMsg]);

  const toggle = (id: string) => {
    if (sel.includes(id)) { setSel(sel.filter((x) => x !== id)); setLimitMsg(false); return; }
    if (sel.length >= MAX) { setLimitMsg(true); return; }
    setSel([...sel, id]);
  };
  const setO = (p: Partial<Office>) => { setOffice((o) => ({ ...o, ...p })); setNeedOffice(false); };

  const download = async (kind: 'pdf' | 'jpg') => {
    if (!office.office.trim()) {
      setNeedOffice(true); setOfficeOpen(true); setTab('input');
      window.setTimeout(() => { if (officeRef.current) { officeRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' }); const i = officeRef.current.querySelector('input'); if (i) i.focus(); } }, 60);
      return;
    }
    setBusy(kind); setDone(null);
    try {
      const { blob, name } = await getFile(ym, cfg, kind);
      const file = new File([blob], name, { type: blob.type });
      const nav = navigator as Navigator & { canShare?: (d: { files: File[] }) => boolean };
      if (kind === 'jpg' && !wide && nav.canShare && nav.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: name }).catch(() => undefined);   // 휴대폰: 공유 시트(카카오톡 · 사진에 저장)
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 4000);
      }
      setDone({ ok: true, text: `저장했어요 · ${name}` });
    } catch (e) {
      setDone({ ok: false, text: e instanceof Error ? e.message : '파일을 만들지 못했어요' });
    } finally { setBusy(''); }
  };

  if (months && !months.length) {
    return (
      <Shell onHome={onHome} title="월간 소식지 만들기" wide={wide}>
        <div style={{ ...card, padding: 28, fontSize: 16, color: X.sub, lineHeight: 1.7 }}>이달 소식지 자료를 준비하고 있어요. 공개되면 바로 만들 수 있어요.</div>
      </Shell>
    );
  }

  const inputCol = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* ① 발행 월 */}
      <Section n={1} of={5} title="몇 월호인가요?">
        {/* 달이 계속 늘어나므로 펼침 목록(2026-10-09 지도사 「밑으로 열리게 해서 선택」) — 최근 달이 위 */}
        <select id="tx-ym" aria-label="발행 월" value={ym} onChange={(e) => setYm(e.target.value)} style={{ ...inputSt, fontWeight: 600, cursor: 'pointer' }}>
          {(months || []).map((m) => <option key={m.ym} value={m.ym}>{ymLabel(m.ym)}</option>)}
        </select>
      </Section>

      {/* ② 받는 고객 */}
      <Section n={2} of={5} title="받는 고객" hint="조건에 맞는 지원사업을 추천해 드려요.">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 12, marginBottom: 20 }}>
          <div>
            <Label htmlFor="tx-sido">지역</Label>
            <select id="tx-sido" value={cond.sido} onChange={(e) => setCond({ ...cond, sido: e.target.value, sigungu: '' })} style={inputSt}>
              <option value="">전국</option>
              {SIDO.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <Label htmlFor="tx-sgg" hint="선택">시·군·구</Label>
            <select id="tx-sgg" value={cond.sigungu} disabled={!cond.sido} onChange={(e) => setCond({ ...cond, sigungu: e.target.value })} style={{ ...inputSt, opacity: cond.sido ? 1 : 0.6 }}>
              <option value="">전체</option>
              {(REGION_CITIES[cond.sido] || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <Label>업종</Label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 20 }}>
          <Chip on={!cond.industry} onClick={() => setCond({ ...cond, industry: '' })}>업종 무관</Chip>
          {INDUSTRIES.map((s) => <Chip key={s} on={cond.industry === s} onClick={() => setCond({ ...cond, industry: s })}>{s}</Chip>)}
        </div>
        <Label>기업 규모</Label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <Chip on={cond.sizes.length !== 1} onClick={() => setCond({ ...cond, sizes: [] })}>규모 무관</Chip>
          {SIZES.map((s) => <Chip key={s} on={cond.sizes.length === 1 && cond.sizes[0] === s} onClick={() => setCond({ ...cond, sizes: [s] })}>{s}</Chip>)}
        </div>
      </Section>

      {/* ③ 지원사업 */}
      <Section n={3} of={5} title="이달의 지원사업" hint="조건에 맞는 사업 중 최대 7개 · 마감 빠른 순" right={<span style={{ fontSize: 15, fontWeight: 700, color: sel.length ? X.blue : X.muted, fontVariantNumeric: 'tabular-nums' }}>{sel.length} / {MAX}</span>}>
        {loadErr && <div role="alert" style={{ color: X.red, background: X.redLt, borderRadius: 10, padding: '10px 12px', fontSize: 15, marginBottom: 10 }}>{loadErr}</div>}
        {!data && !loadErr && <div style={{ color: X.muted, fontSize: 15, padding: '12px 0' }}>불러오는 중…</div>}
        {data && !data.programs.length && <div style={{ color: X.sub, fontSize: 15, padding: '12px 0', lineHeight: 1.6 }}>조건에 맞는 사업이 없어요. 지역을 「전국」으로 하거나 업종을 「업종 무관」으로 바꿔 보세요.</div>}
        {data && data.programs.length > 0 && data.programs.length < MAX && <div style={{ color: X.sub, fontSize: 14, marginBottom: 10 }}>조건에 맞는 사업 {data.programs.length}개를 모두 넣었어요.</div>}
        {limitMsg && <div role="status" style={{ color: X.orange, background: X.orangeLt, borderRadius: 10, padding: '10px 12px', fontSize: 15, marginBottom: 10 }}>7개까지 넣을 수 있어요. 하나를 빼고 골라 주세요.</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data && data.programs.map((p) => {
            const on = sel.includes(p.id);
            const full = sel.length >= MAX;
            return (
              <button key={p.id} type="button" role="checkbox" aria-checked={on} onClick={() => toggle(p.id)} style={{
                display: 'flex', gap: 12, alignItems: 'flex-start', textAlign: 'left', width: '100%', padding: '14px 16px', borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
                border: `1.5px solid ${on ? X.blue : X.line}`, background: on ? X.blueLt : X.card, opacity: !on && full ? 0.45 : 1,
              }}>
                <span aria-hidden style={{ flex: 'none', marginTop: 1, width: 22, height: 22, borderRadius: 7, background: on ? X.blue : X.dot, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Glyph name="check" size={12} color="#fff" stroke={3.5} /></span>
                <span style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: X.text, minWidth: 0 }}>{p.n}</span>
                    <span style={{ flex: 'none', fontSize: 14, fontWeight: 700, color: '#A0640B' }}>{p.a}</span>
                  </span>
                  <span style={{ fontSize: 13, color: X.sub }}>{p.k} · {p.o} · 모집 {p.d}</span>
                  <span style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6, marginTop: 2 }}>
                    {p.x === '마감 임박' && <Tag tone="orange">마감 임박</Tag>}
                    {/월 접수$/.test(p.x) && <Tag tone="purple">{p.x}</Tag>}
                    {p.nationwide ? <Tag tone="gray">전국</Tag> : <Tag tone="blue">{p.local || cond.sido || '지역'}</Tag>}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      </Section>

      {/* ④ 이달의 이슈 */}
      <Section n={4} of={5} title="이달의 이슈" hint="하나만 골라 주세요.">
        {data && !data.issues.length && <div style={{ color: X.sub, fontSize: 15 }}>이달 이슈를 준비하고 있어요.</div>}
        <div role="radiogroup" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data && data.issues.map((i) => {
            const on = issueId === i.id;
            return (
              <button key={i.id} type="button" role="radio" aria-checked={on} onClick={() => setIssueId(i.id)} style={{
                textAlign: 'left', padding: '14px 16px', borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit', transition: 'all .15s',
                border: `1.5px solid ${on ? X.blue : X.line}`, background: on ? X.blueLt : X.card, display: 'flex', gap: 12, alignItems: 'flex-start',
              }}>
                <span style={{ marginTop: 1 }}><Dot on={on} /></span>
                <span style={{ flex: 1 }}>
                  <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}><Tag tone="blue">{i.kind}</Tag>{i.star && <Tag tone="orange">추천</Tag>}</span>
                  <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: X.text, marginTop: 8, lineHeight: 1.45, whiteSpace: 'pre-line' }}>{i.title}</span>
                  <span style={{ display: 'block', fontSize: 13, color: X.sub, marginTop: 4, lineHeight: 1.55 }}>{i.body}</span>
                </span>
              </button>
            );
          })}
        </div>
      </Section>

      {/* ⑤ 보내는 곳 */}
      <div ref={officeRef}>
        <Section n={5} of={5} title="보내는 곳" hint="다음 달에도 그대로 쓸 수 있게 저장해 둘게요."
          right={!officeOpen && office.office ? <Btn kind="ghost" onClick={() => setOfficeOpen(true)} style={{ height: 40, padding: '0 10px', color: X.blue }}>고치기</Btn> : null}>
          {!officeOpen && office.office
            ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                {office.logo && <img src={office.logo} alt="" style={{ width: 44, height: 44, objectFit: 'contain', borderRadius: 6, border: `1px solid ${X.line}` }} />}
                <span style={{ fontSize: 16, color: X.text, lineHeight: 1.5 }}><b>{office.office}</b><br /><span style={{ color: X.sub }}>{[office.role, office.person].filter(Boolean).join(' ')}{(office.tel || office.mob) ? ' · ' + [office.tel, office.mob].filter(Boolean).join(' · ') : ''}</span></span>
              </div>
            )
            : <OfficeForm office={office} setO={setO} need={needOffice} onDone={office.office ? () => setOfficeOpen(false) : undefined} />}
        </Section>
      </div>

      <div style={{ ...card, padding: '18px 24px', display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <span style={{ flex: 'none', fontSize: 14, fontWeight: 700, color: X.text }}>세무일정</span>
        <span style={{ fontSize: 13, color: X.sub, lineHeight: 1.55 }}>그 달 신고·납부 마감일이 달력에 자동으로 들어가요. 주말·공휴일이면 다음 영업일로 바뀌어요.</span>
      </div>
    </div>
  );

  const actions = (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
        <Btn onClick={() => download('pdf')} busy={busy === 'pdf'} style={{ height: 54 }}>{busy === 'pdf' ? '만드는 중…' : 'PDF 다운로드'}</Btn>
        <Btn onClick={() => download('jpg')} busy={busy === 'jpg'} kind="secondary" style={{ height: 54 }}>{busy === 'jpg' ? '만드는 중…' : 'JPG 저장'}</Btn>
      </div>
      {wide && <div style={{ fontSize: 13, color: X.muted, marginTop: 10, textAlign: 'center' }}>PDF는 인쇄·카톡 파일 전송용, JPG는 카톡에서 사진으로 바로 보는 용도예요.</div>}
      {needOffice && <div role="alert" style={{ fontSize: 14, color: X.red, marginTop: 8, textAlign: 'center' }}>5 / 5 보내는 곳의 사무소 이름을 적어 주세요.</div>}
      {done && <div role="status" style={{ fontSize: 14, color: done.ok ? X.green : X.red, marginTop: 8, textAlign: 'center' }}>{done.ok ? '✓ ' : ''}{done.text}</div>}
    </div>
  );

  const preview = <Preview html={html} wide={wide} />;

  if (wide) {
    return (
      <Shell onHome={onHome} title={ym ? `${ymLabel(ym)} 소식지 만들기` : '월간 소식지 만들기'} sub="고르는 대로 오른쪽 미리보기에 바로 반영돼요." wide>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(340px,1fr) minmax(0,520px)', gap: 20, alignItems: 'start' }}>
          {inputCol}
          <div style={{ minWidth: 0, position: 'sticky', top: 76, maxHeight: 'calc(100vh - 92px)', overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {preview}
            <div style={{ ...card, padding: 16 }}>{actions}</div>
          </div>
        </div>
      </Shell>
    );
  }
  return (
    <Shell onHome={onHome} title={ym ? `${ymLabel(ym)} 소식지 만들기` : '월간 소식지 만들기'} wide={false}>
      <div style={{ position: 'sticky', top: 56, zIndex: 5, background: X.bg, padding: '4px 0 12px' }}>
        <div role="tablist" style={{ display: 'flex', background: '#E9EBEE', borderRadius: 12, padding: 4, gap: 4 }}>
          {(['input', 'preview'] as const).map((k) => (
            <button key={k} role="tab" aria-selected={tab === k} type="button" onClick={() => setTab(k)} style={{
              flex: 1, height: 40, borderRadius: 9, border: 'none', background: tab === k ? X.card : 'transparent', color: tab === k ? X.text : X.sub, fontSize: 15, fontWeight: tab === k ? 700 : 600, fontFamily: 'inherit', cursor: 'pointer',
              boxShadow: tab === k ? '0 1px 3px rgba(23,25,28,.08)' : 'none',
            }}>{k === 'input' ? '입력' : '미리보기'}</button>
          ))}
        </div>
      </div>
      {tab === 'input' ? <>{inputCol}<Btn kind="secondary" onClick={() => { setTab('preview'); window.scrollTo(0, 0); }} style={{ width: '100%', marginTop: 12, background: X.card }}>미리보기 보기 →</Btn></> : preview}
      <div style={{ height: 120 }} />
      <div style={{ position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20, background: `linear-gradient(rgba(243,244,246,0), ${X.bg} 28%)`, padding: '24px 16px calc(16px + env(safe-area-inset-bottom))' }}>{actions}</div>
    </Shell>
  );
}

function OfficeForm({ office, setO, need, onDone }: { office: Office; setO: (p: Partial<Office>) => void; need: boolean; onDone?: () => void }) {
  const [custom, setCustom] = React.useState<boolean>(() => !!office.role && !ROLES.includes(office.role));
  const [logoErr, setLogoErr] = React.useState('');
  const pickLogo = (f: File | undefined) => {
    setLogoErr('');
    if (!f) return;
    if (!/^image\/(png|jpeg)$/.test(f.type)) { setLogoErr('PNG · JPG 그림만 올릴 수 있습니다'); return; }
    if (f.size > 280 * 1024) { setLogoErr('300KB 이하 그림을 올려 주세요'); return; }
    const r = new FileReader();
    r.onload = () => setO({ logo: String(r.result || '') });
    r.readAsDataURL(f);
  };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <Label htmlFor="tx-off" hint={`${office.office.length} / 30`}>사무소 이름</Label>
        <KInput id="tx-off" value={office.office} maxLength={30} placeholder="예) 한빛세무회계" onChange={(v) => setO({ office: v })}
          style={need && !office.office ? { borderColor: X.red, boxShadow: `0 0 0 3px ${X.redLt}` } : undefined} />
        {need && !office.office && <div style={{ fontSize: 14, color: X.red, marginTop: 6 }}>사무소 이름을 적어 주세요 (30자까지)</div>}
      </div>
      <div>
        <Label>직함</Label>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {ROLES.map((r) => <Chip key={r} on={!custom && office.role === r} onClick={() => { setCustom(false); setO({ role: r }); }}>{r}</Chip>)}
          <Chip on={custom} onClick={() => { setCustom(true); setO({ role: '' }); }}>직접 입력</Chip>
        </div>
        {custom && <KInput value={office.role} maxLength={12} placeholder="직함" onChange={(v) => setO({ role: v })} style={{ marginTop: 8 }} />}
      </div>
      <div>
        <Label htmlFor="tx-who">담당자 이름</Label>
        <KInput id="tx-who" value={office.person} maxLength={20} placeholder="예) 김한빛" onChange={(v) => setO({ person: v })} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div>
          <Label htmlFor="tx-tel">사무실 전화</Label>
          <input id="tx-tel" inputMode="tel" value={office.tel} placeholder="02-123-4567" onChange={(e) => setO({ tel: phone(e.target.value) })} style={inputSt} />
        </div>
        <div>
          <Label htmlFor="tx-mob">휴대폰</Label>
          <input id="tx-mob" inputMode="tel" value={office.mob} placeholder="010-1234-5678" onChange={(e) => setO({ mob: phone(e.target.value) })} style={inputSt} />
        </div>
      </div>
      <div style={{ fontSize: 13, color: X.muted, marginTop: -8 }}>둘 중 하나만 적어도 돼요. 숫자만 치면 「-」가 자동으로 들어가요.</div>
      <div>
        <Label hint="선택 · 사무소 이름 왼쪽에 들어가요">로고</Label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 52, height: 52, borderRadius: 12, border: `1.5px dashed ${X.lineDk}`, display: 'grid', placeItems: 'center', background: '#FAFBFC', overflow: 'hidden', flexShrink: 0 }}>
            {office.logo ? <img src={office.logo} alt="로고 미리보기" style={{ width: 44, height: 44, objectFit: 'contain' }} /> : <span style={{ fontSize: 14, color: X.muted }}>없음</span>}
          </span>
          <label style={{ height: 42, padding: '0 16px', borderRadius: 12, border: `1.5px solid ${X.line}`, background: X.card, color: X.text, fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
            {office.logo ? '바꾸기' : '그림 올리기'}
            <input type="file" accept="image/png,image/jpeg" onChange={(e) => { pickLogo(e.target.files ? e.target.files[0] : undefined); e.target.value = ''; }} style={{ display: 'none' }} />
          </label>
          {office.logo && <Btn kind="ghost" onClick={() => setO({ logo: '' })} style={{ height: 44, padding: '0 10px' }}>빼기</Btn>}
        </div>
        {logoErr && <div style={{ fontSize: 14, color: X.red, marginTop: 6 }}>{logoErr}</div>}
      </div>
      <div>
        <Label>대표색</Label>
        <div role="radiogroup" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {COLORS.map(([k, name, hex]) => (
            <button key={k} type="button" role="radio" aria-checked={office.color === k} onClick={() => setO({ color: k })} style={{
              height: 42, padding: '0 14px 0 10px', borderRadius: 12, display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, transition: 'all .15s',
              border: `1.5px solid ${office.color === k ? X.blue : X.line}`, background: office.color === k ? X.blueLt : X.card, color: office.color === k ? X.blue : X.label,
            }}><span aria-hidden style={{ width: 20, height: 20, borderRadius: 6, background: hex }} />{name}</button>
          ))}
        </div>
      </div>
      {onDone && <Btn kind="secondary" onClick={onDone} style={{ alignSelf: 'flex-start' }}>다 적었습니다</Btn>}
    </div>
  );
}

/** A4 미리보기 — 서버가 그린 HTML 을 그대로(파일과 같은 판) · 넓은 화면은 높이 맞춤 · 「크게 보기」는 실제 크기 */
function Preview({ html, wide }: { html: string; wide: boolean }) {
  const box = React.useRef<HTMLDivElement>(null);
  const [scale, setScale] = React.useState(0.6);
  const [big, setBig] = React.useState(false);
  React.useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const fit = () => {
      const w = el.clientWidth;
      const h = wide ? window.innerHeight - 76 - 16 - 64 - 150 : 99999;   // 머리줄 · 미리보기 제목 · 버튼 칸
      setScale(Math.max(0.3, Math.min(1, w / 794, h / 1123)));
    };
    fit();
    window.addEventListener('resize', fit);
    return () => window.removeEventListener('resize', fit);
  }, [wide]);
  const s = big ? 1 : scale;
  return (
    <div style={{ ...card, background: '#E9EBEE', padding: 16, position: 'relative' }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14, padding: '0 4px', gap: 8 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: X.text }}>미리보기 <span style={{ fontWeight: 400, color: X.sub }}>A4 한 장 · 받는 분 화면 그대로</span></span>
        <button type="button" onClick={() => setBig(!big)} style={{ marginLeft: 'auto', height: 34, padding: '0 12px', border: 'none', borderRadius: 9, background: X.card, color: X.label, fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>{big ? '전체 보기' : '크게 보기'}</button>
      </div>
      <div ref={box} style={{ overflow: big ? 'auto' : 'hidden', maxHeight: big && wide ? 'calc(100vh - 260px)' : undefined, display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 794 * s, height: 1123 * s, flexShrink: 0, boxShadow: '0 2px 12px rgba(23,25,28,.08)', background: '#fff' }}>
          {html
            ? <iframe title="소식지 미리보기" srcDoc={html} sandbox="allow-same-origin allow-scripts" style={{ width: 794, height: 1123, border: 0, transform: `scale(${s})`, transformOrigin: '0 0', display: 'block', pointerEvents: big ? 'auto' : 'none' }} />
            : <div style={{ width: '100%', height: '100%', display: 'grid', placeItems: 'center', color: X.muted, fontSize: 15 }}>미리보기를 그리는 중…</div>}
        </div>
      </div>
    </div>
  );
}
